import type {
  SupportEscalationOptions,
  SupportEscalationRequest,
  SupportEscalationResponse,
  SupportInboxResponse,
  SupportInboxUnread,
  SupportThreadResponse,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, withCredentials } from "./api";
import { commonHeaders, jsonOr401 } from "./clinician-api";

// TES-1102 — the admin's Support inbox: the staff side of the two-way Ella
// Support thread. Admin only (base role or the TES-1089 grant).
//
// The patient-scoped calls answer 403 `support_reason_required` without
// today's support access reason for that patient; the route asks for one
// before it opens a thread. They declare the PDL purpose "administration"
// (support, not care), which the access log records.

const BASE = `${API_BASE}/employee-api/v1/support-inbox`;
const thread = (patientId: string) =>
  `${BASE}/patients/${encodeURIComponent(patientId)}`;

function headers(): Record<string, string> {
  return { ...commonHeaders(), "X-Access-Purpose": "administration" };
}

export async function fetchSupportInbox(): Promise<SupportInboxResponse> {
  const res = await apiFetch(`${BASE}/threads`, {
    headers: headers(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function fetchSupportInboxUnread(): Promise<SupportInboxUnread> {
  const res = await apiFetch(`${BASE}/unread`, {
    headers: headers(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function fetchSupportThread(
  patientId: string,
): Promise<SupportThreadResponse> {
  const res = await apiFetch(`${thread(patientId)}/messages`, {
    headers: headers(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function sendSupportReply(
  patientId: string,
  body: string,
): Promise<{ id: string; createdAt: string }> {
  const res = await apiFetch(`${thread(patientId)}/messages`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ body }),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function markSupportThreadRead(patientId: string): Promise<void> {
  const res = await apiFetch(`${thread(patientId)}/read`, {
    method: "POST",
    headers: headers(),
    ...withCredentials,
  });
  await jsonOr401(res);
}

// TES-1110 — "Eskalera till vårdteamet". The options say whether a standard
// redirect reply exists; the form offers it only when one does.
export async function fetchSupportEscalationOptions(): Promise<SupportEscalationOptions> {
  const res = await apiFetch(`${BASE}/escalation-options`, {
    headers: headers(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function escalateSupportThread(
  patientId: string,
  body: SupportEscalationRequest,
): Promise<SupportEscalationResponse> {
  const res = await apiFetch(`${thread(patientId)}/escalations`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOr401(res);
}
